/**
 * SA-1094 (design note §4.4, §4.5): the grid rule and the viewer set, pure.
 *
 * `attachmentMeta` is the one reader of an attachment row's name, size and
 * type (moved from the pane). `splitAttachments` keeps the images that load
 * up front for the grid (`loadsUpFrontAsImage`: a raster type, or an `image`
 * row stored with no type) and everything else as rows; `viewerItems` is
 * the set the dialog steps through: every row with a viewable kind, in
 * stored order.
 */
import type { InboxMessage } from '@/types/inbox'
import { viewableKind } from '@/utils/mediaView'
import type { ViewerItem } from './MediaViewerDialog'

/** SA-979 (design note §4.1): the largest image that loads up front, from a
 * blob: a raster type, or an `image` row stored with no type (SA-1110). */
export const INLINE_IMAGE_MAX_BYTES = 10 * 1024 * 1024

export interface AttachmentMeta {
  filename: string | null
  byteSize: number | null
  mimeType: string | null
}

/** SA-715: an email attachment's link reads its filename. Email only: a
 * staff-sent WhatsApp file's stored name is its content hash, so other
 * channels keep the generic label; a WhatsApp group file keeps the file name
 * its sender gave (SA-946). SA-979: the size when it is a number. SA-981: the
 * stored MIME type, which decides whether a click views the file. */
export function attachmentMeta(message: InboxMessage): AttachmentMeta {
  const meta = message.channelMetadata
  const name = meta?.filename
  const size = meta?.byte_size
  const mime = meta?.mime_type
  const named = message.channel === 'email' || message.channel === 'whatsapp-group'
  return {
    filename: named && typeof name === 'string' && name ? name : null,
    byteSize: typeof size === 'number' && Number.isFinite(size) ? size : null,
    mimeType: typeof mime === 'string' && mime ? mime : null,
  }
}

export interface UpFrontInput {
  mediaType?: string | null
  mimeType?: string | null
  byteSize?: number | null
}

/** SA-1110 (design note R2): the one rule for an image that loads up front,
 * from a blob, as a thumbnail or a grid cell: 10 MiB or less (or of unknown
 * size), and a raster type, or an `image` row stored with no type (a legacy
 * row keeps its thumbnail). A non-raster type is its button instead. */
export function loadsUpFrontAsImage({ mediaType, mimeType, byteSize }: UpFrontInput): boolean {
  if (byteSize != null && byteSize > INLINE_IMAGE_MAX_BYTES) return false
  if (viewableKind(mimeType) === 'image') return true
  return mimeType == null && mediaType === 'image'
}

/** A row that loads up front as an image: a grid cell. */
export function isInlineImage(message: InboxMessage): boolean {
  if (!message.mediaUrl) return false
  const { byteSize, mimeType } = attachmentMeta(message)
  return loadsUpFrontAsImage({ mediaType: message.mediaType, mimeType, byteSize })
}

export function splitAttachments(files: readonly InboxMessage[]): { images: InboxMessage[]; rest: InboxMessage[] } {
  const images: InboxMessage[] = []
  const rest: InboxMessage[] = []
  for (const file of files) (isInlineImage(file) ? images : rest).push(file)
  return { images, rest }
}

/** 1 for one image, 2 for two to four, 3 for five or more. */
export function gridColumns(n: number): 1 | 2 | 3 {
  if (n <= 1) return 1
  return n <= 4 ? 2 : 3
}

export function viewerItems(files: readonly InboxMessage[]): ViewerItem[] {
  const items: ViewerItem[] = []
  for (const file of files) {
    if (!file.mediaUrl) continue
    const { filename, byteSize, mimeType } = attachmentMeta(file)
    const kind = viewableKind(mimeType)
    if (!kind) continue
    items.push({ kind, messageId: file.id, mediaUrl: file.mediaUrl, filename, byteSize })
  }
  return items
}
